/** Bound values are substituted, never computed. A reference resolves or it does not. */

import { modifier, type Entity } from "@nebulize/sdk";

import type { EntityIndex } from "../transport/snapshotStore";
import { entityKey } from "../transport/snapshotStore";
import type { DataBinding } from "./contract";

export interface BoundNumber {
  readonly kind: "number";
  readonly value: number;
  readonly min: number | undefined;
  readonly max: number | undefined;
}

export interface BoundFlag {
  readonly kind: "flag";
  readonly value: boolean;
}

export interface BoundText {
  readonly kind: "text";
  readonly value: string;
}

export type BoundValue = BoundNumber | BoundFlag | BoundText;

export type BoundValues = ReadonlyMap<string, BoundValue>;

function named(value: string | null | undefined): value is string {
  return typeof value === "string" && value.length > 0;
}

/** A tag satisfies a query when it equals it or extends it at a dot. */
export function hasTag(entity: Entity, query: string): boolean {
  const tags = entity.tags?.tags ?? [];
  return tags.some((tag) => tag === query || tag.startsWith(`${query}.`));
}

/**
 * What this entity's `name` reads as, for a renderer to draw: the base with RUN-044's modifier
 * set applied. Undefined when the entity holds no such attribute.
 */
export function currentAttributeValue(entity: Entity, name: string): number | undefined {
  const reading = entity.attributes?.[name];
  if (reading === undefined) return undefined;
  return modifier.evaluateAttribute(reading, entity.modifiers?.[name] ?? []);
}

function readAttribute(entity: Entity, name: string): BoundNumber | undefined {
  const reading = entity.attributes?.[name];
  if (reading === undefined) return undefined;
  return {
    kind: "number",
    value: currentAttributeValue(entity, name) ?? reading.value,
    min: reading.min ?? undefined,
    max: reading.max ?? undefined,
  };
}

function readTextAttribute(entity: Entity, name: string): BoundText | undefined {
  const reading = entity.text_attributes?.[name];
  if (reading === undefined) return undefined;
  return { kind: "text", value: reading.value };
}

/** Resolve each binding against the held store, or against `item` when it names no entity. */
export function resolveBindings(
  bindings: readonly DataBinding[],
  entities: EntityIndex,
  item?: Entity,
): BoundValues {
  const values = new Map<string, BoundValue>();

  for (const binding of bindings) {
    const entity =
      binding.entity === null || binding.entity === undefined
        ? item
        : entities.get(entityKey(binding.entity.scope, binding.entity.entity_id));
    if (entity === undefined) continue;

    if (named(binding.attribute)) {
      const reading = readAttribute(entity, binding.attribute);
      if (reading !== undefined) values.set(binding.key, reading);
      continue;
    }

    if (named(binding.text_attribute)) {
      const reading = readTextAttribute(entity, binding.text_attribute);
      if (reading !== undefined) values.set(binding.key, reading);
      continue;
    }

    if (named(binding.tag)) {
      values.set(binding.key, { kind: "flag", value: hasTag(entity, binding.tag) });
    }
  }

  return values;
}

/** Every tag predicate the node declares holds. An unresolved predicate does not hold. */
export function predicatesHold(bindings: readonly DataBinding[], values: BoundValues): boolean {
  for (const binding of bindings) {
    if (named(binding.attribute) || !named(binding.tag)) continue;
    const value = values.get(binding.key);
    if (value?.kind !== "flag" || !value.value) return false;
  }
  return true;
}

/** Draw a bound number at three decimals without changing what the store holds. */
export function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return "";
  if (Number.isInteger(value)) return String(value);
  return String(Number(value.toFixed(3)));
}

const PLACEHOLDER = /\{\{|\}\}|\{([^{}]*)\}/g;

/**
 * Substitute bound values into a format string, the only expression a scene has.
 *
 * `{{` and `}}` are literal braces. An unresolved placeholder and a flag both substitute to
 * nothing; a flag gates its node instead of printing.
 */
export function formatText(template: string | null | undefined, values: BoundValues): string {
  if (template === null || template === undefined) return "";

  return template.replace(PLACEHOLDER, (match: string, key: string | undefined) => {
    if (match === "{{") return "{";
    if (match === "}}") return "}";
    const value = key === undefined ? undefined : values.get(key);
    if (value?.kind === "number") return formatNumber(value.value);
    if (value?.kind === "text") return value.value;
    return "";
  });
}
